import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { MenuItemDetailsDialog } from '@/components/MenuItemDetailsDialog';

const BUBBLES_RESTAURANT_IDS = [
  '2874e157-906f-4254-ba53-b96d30034b03',
  'b753580d-e089-4238-a745-0abee68e4cf1',
  '2dbd04da-eb41-460f-842d-700eb034d719'
];

interface MenuItem {
  id: string;
  name: string;
  price: number;
  image_url?: string;
  description?: string;
  original_price?: number;
  is_customizable?: boolean;
}

interface SelectedCustomization {
  customization_id: string;
  selected_options: string[];
  text_input?: string;
}

interface ComplementYourMealProps {
  onAddItem: (
    item: MenuItem,
    restaurantId: string,
    restaurantName: string,
    customizations?: SelectedCustomization[],
    quantity?: number,
    finalUnitPrice?: number
  ) => void;
  currentRestaurantId: string;
  deliveryType?: 'delivery' | 'pickup';
}

const COMPLEMENT_CACHE_KEY = 'gambgo:complementMeal:v2';

interface CachedComplement {
  restaurantId: string;
  restaurantName: string;
  items: MenuItem[];
}

const ComplementYourMeal = ({ onAddItem, currentRestaurantId, deliveryType = 'delivery' }: ComplementYourMealProps) => {

  const cached: CachedComplement | null = (() => {
    try {
      const raw = localStorage.getItem(COMPLEMENT_CACHE_KEY);
      return raw ? (JSON.parse(raw) as CachedComplement) : null;
    } catch {
      return null;
    }
  })();

  const [bubblesItems, setBubblesItems] = useState<MenuItem[]>(cached?.items || []);
  const [bubblesRestaurantId, setBubblesRestaurantId] = useState<string | null>(cached?.restaurantId || null);
  const [bubblesRestaurantName, setBubblesRestaurantName] = useState<string>(cached?.restaurantName || '');
  const [detailsItem, setDetailsItem] = useState<MenuItem | null>(null);

  useEffect(() => {
    fetchBubblesTropicsItems();
  }, []);

  const fetchBubblesTropicsItems = async () => {
    try {
      // Use known Bubbles restaurant IDs directly to avoid slow ilike lookups
      const { data: restaurants, error: restError } = await supabase
        .from('restaurants')
        .select('id, name')
        .in('id', BUBBLES_RESTAURANT_IDS)
        .limit(1);

      if (restError || !restaurants || restaurants.length === 0) {
        return;
      }

      const restaurant = restaurants[0];
      setBubblesRestaurantId(restaurant.id);
      setBubblesRestaurantName(restaurant.name);

      const { data: items, error: itemsError } = await supabase
        .from('menu_items')
        .select('id, name, price, image_url, description, original_price, is_customizable')
        .eq('restaurant_id', restaurant.id)
        .eq('is_available', true)
        .order('name', { ascending: true });

      if (itemsError || !items) return;

      setBubblesItems(items);
      try {
        localStorage.setItem(
          COMPLEMENT_CACHE_KEY,
          JSON.stringify({ restaurantId: restaurant.id, restaurantName: restaurant.name, items })
        );
      } catch {}
    } catch (error) {
      console.error('Error loading Bubbles Tropics:', error);
    }
  };

  const handleAddItem = (item: MenuItem) => {
    if (!bubblesRestaurantId) {
      toast.error('Restaurant information not available');
      return;
    }
    if (item.is_customizable) {
      setDetailsItem(item);
      return;
    }
    onAddItem(item, bubblesRestaurantId, bubblesRestaurantName);
  };


  // Don't show if Bubbles is the current restaurant or no items loaded yet
  if (bubblesRestaurantId === currentRestaurantId || !bubblesItems.length) {
    return null;
  }

  const getEffectivePrice = (item: MenuItem) =>
    bubblesRestaurantId && BUBBLES_RESTAURANT_IDS.includes(bubblesRestaurantId) && deliveryType === 'pickup' && item.original_price
      ? item.original_price
      : item.price;

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-2">Complement your meal with no extra delivery fee</h3>
      <p className="text-sm text-muted-foreground mb-4">Add items from {bubblesRestaurantName}</p>
      
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {bubblesItems.map(item => (
          <div 
            key={item.id} 
            className="flex-shrink-0 w-32 bg-card border border-border rounded-lg overflow-hidden cursor-pointer"
            onClick={() => setDetailsItem(item)}
          >
            {item.image_url && (
              <img 
                src={item.image_url} 
                alt={item.name}
                className="w-full h-24 object-cover"
              />
            )}
            <div className="p-2">
              <p className="text-xs font-medium line-clamp-2 mb-1">{item.name}</p>
              <div className="flex items-center justify-between gap-1">
                <div className="flex flex-col items-start">
                  {item.original_price && bubblesRestaurantId && BUBBLES_RESTAURANT_IDS.includes(bubblesRestaurantId) && deliveryType === 'delivery' && (
                    <p className="text-[10px] text-muted-foreground line-through leading-tight">D{item.original_price.toFixed(2)}</p>
                  )}
                  <p className="text-sm font-bold">
                    D{getEffectivePrice(item).toFixed(2)}
                  </p>
                </div>
                <Button 
                  size="icon" 
                  variant="outline"
                  className="h-6 w-6"
                  onClick={(e) => { e.stopPropagation(); handleAddItem(item); }}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <MenuItemDetailsDialog
        isOpen={!!detailsItem}
        onClose={() => setDetailsItem(null)}
        item={detailsItem ? ({
          id: detailsItem.id,
          name: detailsItem.name,
          description: detailsItem.description || '',
          price: detailsItem.price,
          image_url: detailsItem.image_url || '',
          is_customizable: !!detailsItem.is_customizable,
          original_price: detailsItem.original_price,
        }) : null}
        displayPrice={detailsItem ? getEffectivePrice(detailsItem) : undefined}
        originalPrice={
          detailsItem && detailsItem.original_price && deliveryType === 'delivery'
            ? detailsItem.original_price
            : undefined
        }
        canOrder={true}
        onAddToCart={(it, qty, customizations, finalUnitPrice) => {
          if (!bubblesRestaurantId) {
            toast.error('Restaurant information not available');
            return;
          }
          const base = bubblesItems.find(b => b.id === it.id) || (it as MenuItem);
          onAddItem(base, bubblesRestaurantId, bubblesRestaurantName, customizations, qty, finalUnitPrice);
          setDetailsItem(null);
        }}
      />
    </div>
  );
};


export default ComplementYourMeal;
